import React, { useState, useEffect, useRef } from 'react';
import { 
  UserOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  HomeOutlined, 
  CalendarOutlined, 
  PictureOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CameraOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Upload, 
  Avatar, 
  Typography, 
  Row, 
  Col, 
  Space, 
  Tag, 
  Divider, 
  message, 
  Modal,
  DatePicker,
  Select,
  Alert
} from 'antd';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import axios from 'axios';
import dayjs from 'dayjs';
import OTPVerification from './OTPVerification';

const { Text, Title } = Typography;
const { Option } = Select;

const UserProfile = ({ visible, onClose }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [addressProofFile, setAddressProofFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [otpType, setOtpType] = useState('mobile'); // 'mobile' or 'email'
  const [otpValue, setOtpValue] = useState('');
  const [profilePreviewUrl, setProfilePreviewUrl] = useState('');
  const profilePreviewRef = useRef('');

  const getUploadsBaseUrl = () => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || `${window.location.origin}/api`;
    return apiBase.replace(/\/api\/?$/, '');
  };

  const resolveProfileImageUrl = (value) => {
    if (!value) return '';
    if (
      String(value).startsWith('http://') ||
      String(value).startsWith('https://') ||
      String(value).startsWith('blob:') ||
      String(value).startsWith('data:')
    ) return value;
    if (String(value).startsWith('/uploads/')) return `${getUploadsBaseUrl()}${value}`;
    return `${getUploadsBaseUrl()}/uploads/${value}`;
  };

  const setSafeProfilePreview = (nextUrl) => {
    profilePreviewRef.current = nextUrl;
    setProfilePreviewUrl((prev) => {
      if (prev && prev.startsWith('blob:') && prev !== nextUrl) {
        URL.revokeObjectURL(prev);
      }
      return nextUrl;
    });
  };

  useEffect(() => {
    return () => {
      const preview = profilePreviewRef.current;
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, []);

  useEffect(() => {
    if (visible && user?.id) {
      // Pre-fill with auth data first, then fetch additional profile data
      prefillWithAuthData();
      fetchUserProfile();
    }
  }, [visible, user?.id]);

  const prefillWithAuthData = () => {
    if (user) {
      console.log('Prefilling with auth data:', user);
      
      // Pre-fill form with data from auth API (only fields that exist)
      form.setFieldsValue({
        name: user.name || '',
        email: user.email || '',
        mobile: '', // Will be filled from profile API
        dateOfBirth: null, // Will be filled from profile API
        address: '', // Will be filled from profile API
        city: '', // Will be filled from profile API
        state: '', // Will be filled from profile API
        pincode: '' // Will be filled from profile API
      });
      
      // Set initial user data with only available fields
      setUserData({
        id: user.id,
        name: user.name,
        email: user.email,
        mobile: null,
        dateOfBirth: null,
        address: null,
        city: null,
        state: null,
        pincode: null,
        profileImage: user.profileImage,
        mobileVerified: false,
        emailVerified: false,
        addressProof: null,
        addressProofVerified: false,
        role: user.role,
        status: user.status,
        areaId: user.areaId
      });
    }
  };

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || user?.token;
      const response = await axios.get(`/api/users/${user.id}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.data.success) {
        const profileData = response.data.data;
        console.log('Profile data from API:', profileData);
        
        setUserData(profileData);
        
        // Update form with profile data (including any new fields from database)
        const formData = {
          name: profileData.name || '',
          email: profileData.email || '',
          mobile: profileData.mobile || '',
          dateOfBirth: profileData.dateOfBirth ? dayjs(profileData.dateOfBirth) : null,
          address: profileData.address || '',
          city: profileData.city || '',
          state: profileData.state || '',
          pincode: profileData.pincode || ''
        };
        
        console.log('Setting form data:', formData);
        form.setFieldsValue(formData);
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      message.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (values) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || user?.token;
      
      const profileData = {
        ...values,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('YYYY-MM-DD') : null
      };

      const response = await axios.put(`/api/users/${user.id}/profile`, profileData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        // Check if verification is required
        if (response.data.verificationRequired && response.data.verificationRequired.length > 0) {
          const verificationList = response.data.verificationRequired.join(' and ');
          message.success(`Profile updated successfully. Please verify your new ${verificationList}.`);
          
          // Show verification requirements
          if (response.data.verificationRequired.includes('mobile')) {
            message.info('Your mobile number has changed. Please verify the new number.');
          }
          if (response.data.verificationRequired.includes('email')) {
            message.info('Your email address has changed. Please verify the new email.');
          }
          if (response.data.verificationRequired.includes('addressProof')) {
            message.info('Your address has changed. Please upload and verify new address proof.');
          }
        } else {
          message.success('Profile updated successfully');
        }
        
        setEditing(false);
        fetchUserProfile();
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      message.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressProofUpload = async (file) => {
    try {
      setUploadingProof(true);
      const token = localStorage.getItem('token') || user?.token;
      
      const formData = new FormData();
      formData.append('addressProof', file);
      formData.append('userId', user.id);

      const response = await axios.post('/api/users/upload-address-proof', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        message.success('Address proof uploaded successfully');
        fetchUserProfile();
      }
    } catch (error) {
      console.error('Failed to upload address proof:', error);
      message.error('Failed to upload address proof');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleProfileImageUpload = async (file) => {
    const localPreview = URL.createObjectURL(file);
    setSafeProfilePreview(localPreview);

    try {
      const token = localStorage.getItem('token') || user?.token;
      
      const formData = new FormData();
      formData.append('profileImage', file);

      const response = await axios.post(`/api/users/${user.id}/profile-image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        message.success('Profile image updated successfully');
        const uploadedImage = response.data?.data?.imageUrl || '';
        if (uploadedImage) {
          setUserData((prev) => ({
            ...(prev || {}),
            profileImage: uploadedImage
          }));
          setSafeProfilePreview('');
        }
        fetchUserProfile();
      }
    } catch (error) {
      console.error('Failed to upload profile image:', error);
      message.error('Failed to upload profile image');
      setSafeProfilePreview('');
    }
  };

  const confirmProfileImageUpload = (file) => {
    Modal.confirm({
      title: 'Confirm profile photo change',
      content: `Do you want to update your profile photo to "${file.name}"?`,
      okText: 'Yes, Update',
      cancelText: 'Cancel',
      onOk: async () => {
        await handleProfileImageUpload(file);
      }
    });
    return false;
  };

  const handleVerifyMobile = () => {
    const currentMobile = String(form.getFieldValue('mobile') || userData?.mobile || '').trim();
    if (!currentMobile) {
      message.warning('Please add a mobile number first');
      return;
    }
    if (currentMobile !== String(userData?.mobile || '').trim()) {
      message.warning('Please save your updated mobile number first, then verify.');
      return;
    }
    setOtpType('mobile');
    setOtpValue(currentMobile);
    setOtpModalVisible(true);
  };

  const handleVerifyEmail = () => {
    const currentEmail = String(form.getFieldValue('email') || userData?.email || '').trim();
    if (!currentEmail) {
      message.warning('Please add an email address first');
      return;
    }
    if (currentEmail !== String(userData?.email || '').trim()) {
      message.warning('Please save your updated email first, then verify.');
      return;
    }
    setOtpType('email');
    setOtpValue(currentEmail);
    setOtpModalVisible(true);
  };

  const handleVerificationSuccess = () => {
    message.success('Verification successful!');
    fetchUserProfile(); // Refresh profile data
  };

  const getVerificationStatus = (isVerified) => {
    return isVerified ? (
      <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag>
    ) : (
      <Tag icon={<CloseCircleOutlined />} color="error">Not Verified</Tag>
    );
  };

  const getVerificationColor = (isVerified) => {
    return isVerified ? '#52c41a' : '#ff4d4f';
  };

  const showVerificationRequirements = () => {
    if (!userData) return null;
    
    const requirements = [];
    if (!userData.mobileVerified) requirements.push('Mobile Number');
    if (!userData.emailVerified) requirements.push('Email Address');
    if (!userData.addressProofVerified) requirements.push('Address Proof');
    
    if (requirements.length === 0) return null;

    const canCreatePost = Boolean(userData.mobileVerified && userData.emailVerified);
    const postingNote = canCreatePost
      ? 'Post creation is enabled.'
      : 'Verify both Mobile Number and Email Address to enable post creation.';
    
    return (
      <Alert
        message="Verification Required"
        description={`Please verify your: ${requirements.join(', ')}. ${postingNote}`}
        type="warning"
        showIcon
        style={{ marginTop: '16px', marginBottom: '8px' }}
      />
    );
  };

  if (!visible) return null;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UserOutlined />
          User Profile
        </div>
      }
      open={visible}
      onCancel={onClose}
      style={{ top: 24 }}
      bodyStyle={{ maxHeight: '74vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}
      footer={
        editing ? (
          <Space>
            <Button onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              form="userProfileForm"
              loading={loading}
              icon={<SaveOutlined />}
            >
              Save Changes
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button
              type="primary"
              onClick={() => setEditing(true)}
              icon={<EditOutlined />}
            >
              Edit Profile
            </Button>
          </Space>
        )
      }
      width={800}
      destroyOnClose
    >
      <div style={{ padding: '20px 0' }}>
        {/* Profile Header */}
        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <Avatar 
                size={120} 
                src={profilePreviewUrl || resolveProfileImageUrl(userData?.profileImage)} 
                icon={<UserOutlined />}
                style={{ border: '4px solid #f0f0f0' }}
              />
              <div style={{ marginTop: '12px' }}>
                <Upload
                  name="profileImage"
                  showUploadList={false}
                  beforeUpload={confirmProfileImageUpload}
                >
                  <Button icon={<CameraOutlined />} size="small">
                    Change Photo
                  </Button>
                </Upload>
              </div>
            </div>
          </Col>
          
          <Col span={16}>
            <div>
              <Title level={3} style={{ margin: '0 0 8px 0' }}>
                {userData?.name || 'User Name'}
              </Title>
              
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong>Mobile: </Text>
                  <Text>{userData?.mobile || 'Not provided'}</Text>
                  {getVerificationStatus(userData?.mobileVerified)}
                </div>
                
                <div>
                  <Text strong>Email: </Text>
                  <Text>{userData?.email || 'Not provided'}</Text>
                  {getVerificationStatus(userData?.emailVerified)}
                </div>
                
                <div>
                  <Text strong>Address Proof: </Text>
                  {getVerificationStatus(userData?.addressProofVerified)}
                </div>
              </Space>

              <Divider />

                    {/* Verification Status Summary */}
      <Row gutter={16}>
        <Col span={8}>
          <Card size="small" style={{ textAlign: 'center', borderColor: getVerificationColor(userData?.mobileVerified) }}>
            <div style={{ color: getVerificationColor(userData?.mobileVerified) }}>
              <PhoneOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Mobile</div>
              <div style={{ fontSize: '12px' }}>
                {userData?.mobileVerified ? 'Verified' : 'Not Verified'}
              </div>
            </div>
          </Card>
        </Col>
        
        <Col span={8}>
          <Card size="small" style={{ textAlign: 'center', borderColor: getVerificationColor(userData?.addressProofVerified) }}>
            <div style={{ color: getVerificationColor(userData?.addressProofVerified) }}>
              <HomeOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Address</div>
              <div style={{ fontSize: '12px' }}>
                {userData?.addressProofVerified ? 'Verified' : 'Not Verified'}
              </div>
            </div>
          </Card>
        </Col>
        
        <Col span={8}>
          <Card size="small" style={{ textAlign: 'center', borderColor: userData?.mobileVerified && userData?.emailVerified ? '#52c41a' : '#ff4d4f' }}>
            <div style={{ color: userData?.mobileVerified && userData?.emailVerified ? '#52c41a' : '#ff4d4f' }}>
              <EditOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Can Post</div>
              <div style={{ fontSize: '12px' }}>
                {userData?.mobileVerified && userData?.emailVerified ? 'Yes' : 'No'}
              </div>
            </div>
          </Card>
        </Col>
      </Row>
            </div>
          </Col>
        </Row>

        {/* Profile Form */}
        <Form
          id="userProfileForm"
          form={form}
          layout="vertical"
          onFinish={handleSaveProfile}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Enter your full name" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="mobile"
                label="Mobile Number"
                rules={[
                  { required: true, message: 'Please enter your mobile number' },
                  { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit mobile number' }
                ]}
              >
                <Input prefix={<PhoneOutlined />} placeholder="Enter mobile number" />
              </Form.Item>
              <Button 
                type="link" 
                icon={<SafetyOutlined />}
                onClick={handleVerifyMobile}
                style={{ padding: 0, marginTop: '4px' }}
              >
                Verify Mobile Number
              </Button>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Enter email address" />
              </Form.Item>
              <Button 
                type="link" 
                icon={<SafetyOutlined />}
                onClick={handleVerifyEmail}
                style={{ padding: 0, marginTop: '4px' }}
              >
                Verify Email Address
              </Button>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="dateOfBirth"
                label="Date of Birth"
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  placeholder="Select date of birth"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Address"
          >
            <Input.TextArea 
              prefix={<HomeOutlined />} 
              placeholder="Enter your complete address"
              rows={3}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="city"
                label="City"
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="state"
                label="State"
              >
                <Input placeholder="Enter state" />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="pincode"
                label="Pincode"
                rules={[
                  { pattern: /^[0-9]{6}$/, message: 'Please enter a valid 6-digit pincode' }
                ]}
              >
                <Input placeholder="Enter pincode" />
              </Form.Item>
            </Col>
          </Row>

          {/* Address Proof Upload */}
          <Divider>Address Proof</Divider>
          
          <Alert
            message="Address Proof Required for Rating Representatives"
            description="You need to upload and verify your address proof to rate area representatives. Only verified users can rate representatives."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          {userData?.addressProof ? (
            <div style={{ marginBottom: '16px' }}>
              <Text strong>Current Address Proof: </Text>
              <Text>{userData.addressProof}</Text>
              {getVerificationStatus(userData.addressProofVerified)}
            </div>
          ) : (
            <div style={{ marginBottom: '16px' }}>
              <Text type="secondary">No address proof uploaded yet</Text>
            </div>
          )}

          <Upload
            name="addressProof"
            showUploadList={false}
            beforeUpload={(file) => {
              handleAddressProofUpload(file);
              return false;
            }}
            disabled={uploadingProof}
          >
            <Button 
              icon={<UploadOutlined />} 
              loading={uploadingProof}
              disabled={userData?.addressProofVerified}
            >
              {userData?.addressProofVerified ? 'Address Proof Verified' : 'Upload Address Proof'}
            </Button>
          </Upload>

          {/* Verification info moved to bottom as requested */}
          {showVerificationRequirements()}

        </Form>
      </div>

      {/* OTP Verification Modal */}
      <OTPVerification
        visible={otpModalVisible}
        onClose={() => setOtpModalVisible(false)}
        type={otpType}
        currentValue={otpValue}
        onVerificationSuccess={handleVerificationSuccess}
      />
    </Modal>
  );
};

export default UserProfile;
